import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Syringe, Stethoscope, Pill, Bell, CalendarDays, Trash2, Check, Loader2 } from 'lucide-react';
import { format, isBefore, startOfDay, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { useApp } from '../../context/AppContext';
import api from '../../api';
import { Input } from '../../components/ui/input';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const TYPES = [
  { value: 'vaccine', label: 'Vaccine', icon: Syringe },
  { value: 'vet-visit', label: 'Vet visit', icon: Stethoscope },
  { value: 'medication', label: 'Medication', icon: Pill },
  { value: 'other', label: 'Other', icon: Bell },
];
const typeMeta = (t) => TYPES.find((x) => x.value === t) || TYPES[3];

export default function Reminders() {
  const nav = useNavigate();
  const { cats, selectedCat } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [type, setType] = useState('vaccine');
  const [date, setDate] = useState(undefined);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/reminders'); setItems(r.data.reminders || []); } catch (e) {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!title.trim()) { toast.error('Please add a title'); return; }
    if (!date) { toast.error('Please pick a date'); return; }
    setBusy(true);
    try {
      await api.post('/reminders', { title: title.trim(), type, date: format(date, 'yyyy-MM-dd'), notes, cat_id: selectedCat?.id });
      toast.success('Reminder added');
      setTitle(''); setNotes(''); setDate(undefined); setType('vaccine'); setShowForm(false);
      load();
    } catch (e) { toast.error('Could not save reminder'); }
    finally { setBusy(false); }
  };

  const toggle = async (r) => {
    setItems((p) => p.map((x) => x.id === r.id ? { ...x, done: !x.done } : x));
    try { await api.patch(`/reminders/${r.id}`, { done: !r.done }); } catch (e) { load(); }
  };
  const remove = async (r) => {
    setItems((p) => p.filter((x) => x.id !== r.id));
    try { await api.delete(`/reminders/${r.id}`); } catch (e) { load(); }
  };

  const today = startOfDay(new Date());
  const active = items.filter((i) => !i.done);
  const overdue = active.filter((i) => isBefore(parseISO(i.date), today));
  const upcoming = active.filter((i) => !isBefore(parseISO(i.date), today));
  const done = items.filter((i) => i.done);

  const Row = ({ r, danger }) => {
    const M = typeMeta(r.type); const Icon = M.icon;
    return (
      <div className={`flex items-center gap-3 rounded-2xl border p-3 card-soft ${danger ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-white'} ${r.done ? 'opacity-60' : ''}`}>
        <button onClick={() => toggle(r)} className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 ${r.done ? 'border-[hsl(var(--coral))] bg-[hsl(var(--coral))]' : 'border-border'}`}>
          {r.done && <Check className="h-4 w-4 text-white" />}
        </button>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent"><Icon className="h-5 w-5 text-[hsl(var(--coral))]" /></div>
        <div className="min-w-0 flex-1">
          <p className={`truncate font-bold ${r.done ? 'line-through' : ''}`}>{r.title}</p>
          <p className={`text-xs ${danger ? 'font-semibold text-destructive' : 'text-muted-foreground'}`}>{M.label} · {format(parseISO(r.date), 'EEE, MMM d, yyyy')}{danger ? ' · overdue' : ''}</p>
          {r.notes ? <p className="truncate text-xs text-muted-foreground">{r.notes}</p> : null}
        </div>
        <button onClick={() => remove(r)} className="p-2 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-background/95 px-3 py-3 backdrop-blur">
        <button onClick={() => nav('/app/profile')} className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-secondary"><ChevronLeft className="h-5 w-5" /></button>
        <div className="flex-1"><h1 className="text-base font-extrabold leading-tight">Care reminders</h1><p className="text-xs text-muted-foreground">Never miss a vaccine or vet visit</p></div>
        <button onClick={() => setShowForm((v) => !v)} className="flex h-9 w-9 items-center justify-center rounded-full coral-btn text-white"><Plus className="h-5 w-5" /></button>
      </header>

      <div className="space-y-5 px-4 py-4">
        {showForm && (
          <div className="animate-fade-up space-y-3 rounded-2xl bg-white p-4 card-soft">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Rabies booster" className="h-11 rounded-xl bg-white" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="h-11 rounded-xl bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="flex h-11 items-center gap-2 rounded-xl border border-input bg-white px-3 text-sm">
                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                    <span className={date ? '' : 'text-muted-foreground'}>{date ? format(date, 'MMM d, yyyy') : 'Pick date'}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="h-11 rounded-xl bg-white" />
            <button onClick={add} disabled={busy} className="coral-btn flex w-full items-center justify-center gap-2 rounded-xl py-3 font-bold text-white disabled:opacity-70">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Add reminder</button>
          </div>
        )}

        {loading && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--coral))]" /></div>}

        {!loading && items.length === 0 && !showForm && (
          <div className="rounded-2xl bg-secondary/60 p-6 text-center">
            <Bell className="mx-auto h-8 w-8 text-[hsl(var(--coral))]" />
            <p className="mt-2 font-bold">No reminders yet</p>
            <p className="text-sm text-muted-foreground">Add vaccine dates, vet visits and medication schedules to stay on top of {selectedCat?.name}'s care.</p>
            <button onClick={() => setShowForm(true)} className="coral-btn mt-4 rounded-full px-5 py-2.5 text-sm font-bold text-white">Add your first reminder</button>
          </div>
        )}

        {overdue.length > 0 && <div className="space-y-2"><p className="text-xs font-bold uppercase tracking-wide text-destructive">Overdue</p>{overdue.map((r) => <Row key={r.id} r={r} danger />)}</div>}
        {upcoming.length > 0 && <div className="space-y-2"><p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Upcoming</p>{upcoming.map((r) => <Row key={r.id} r={r} />)}</div>}
        {done.length > 0 && <div className="space-y-2"><p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Completed</p>{done.map((r) => <Row key={r.id} r={r} />)}</div>}
      </div>
    </div>
  );
}
